(() => {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 5900;
  const VITALSOP_PRODUCT_ID = 9781012365634;
  const OFFER_VARIANTS = new Set(['56022016655682', '56022016688450', '56022016721218']);
  const GIFT_VARIANTS = {
    countertop: 54953957130562,
    tray: 55972557553986,
    pouch: 55972557521218
  };
  const GIFT_CART_VARIANTS = new Set(Object.values(GIFT_VARIANTS).map(String));
  const CART_UPGRADES = {
    subscribe: {
      variantId: '56022016688450',
      planId: '692749304130',
      supplyDays: '30',
      comparePriceCents: 4500,
      badge: 'SUBSCRIBE & SAVE',
      title: 'Upgrade to Subscribe & Save',
      benefits: [
        'Save 10% on every bottle',
        'Free shipping + Daily Ritual Gift',
        'Skip, pause, or cancel anytime'
      ],
      button: 'Upgrade & Save'
    },
    bestValue: {
      variantId: '56022016721218',
      planId: '692749402434',
      supplyDays: '90',
      comparePriceCents: 13500,
      badge: 'MOST POPULAR',
      title: 'Buy Two, Get One Free',
      benefits: [
        '3rd bottle free every delivery',
        'Free Loyalty Gifts as You Stay',
        'Backed by a 90-Day Guarantee'
      ],
      button: 'Yes — Get 3 Bottles for $79.95'
    }
  };
  const MANAGED_CART_VARIANTS = new Set([
    ...OFFER_VARIANTS,
    ...Object.values(GIFT_VARIANTS).map(String)
  ]);
  let clearMutationTimer;
  let giftCleanupTimer;
  let giftCleanupRunning = false;
  let checkoutResuming = false;
  let enhanceFrame;
  let bundleSubmitting = false;
  let lastKnownCart = null;
  const UPGRADE_TRACKING_STARTED_AT = '2026-08-24T14:30:00-07:00';

  function currentCart() {
    return lastKnownCart;
  }

  function trackingKey(stage, cartToken, targetVariant) {
    return `vitalsop:cart-upgrade:${stage}:${cartToken || 'unknown'}:${targetVariant}`;
  }

  function trackUpgradeEvent(name, payload = {}, dedupeStage = '') {
    const cart = currentCart();
    const targetVariant = String(payload.target_variant_id || CART_UPGRADES.bestValue.variantId);
    const key = dedupeStage ? trackingKey(dedupeStage, cart?.token, targetVariant) : '';
    if (key) {
      try {
        if (sessionStorage.getItem(key)) return false;
        sessionStorage.setItem(key, '1');
      } catch (_) {}
    }
    const event = {
      tracking_started_at: UPGRADE_TRACKING_STARTED_AT,
      cart_token: cart?.token || '',
      source: 'vitalsop_native_cart_upgrade',
      ...payload
    };
    if (window.__vitalsop_tracking_test_mode) {
      window.__vitalsop_tracking_test_events = window.__vitalsop_tracking_test_events || [];
      window.__vitalsop_tracking_test_events.push({ name, event });
      return true;
    }
    window._learnq = window._learnq || [];
    window._learnq.push(['track', name, event]);
    const normalizedName = name.toLowerCase().replace(/\s+/g, '_');
    if (typeof window.gtag === 'function') {
      window.gtag('event', normalizedName, event);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: normalizedName, ...event });
    }
    try {
      const published = window.Shopify?.analytics?.publish?.(
        normalizedName,
        event
      );
      if (published?.catch) published.catch(() => {});
    } catch (_) {}
    return true;
  }

  function markCartMutating() {
    clearTimeout(clearMutationTimer);
    document.documentElement.classList.add('vitalsop-cart-mutating');
  }

  function clearCartMutatingSoon() {
    if (bundleSubmitting) return;
    clearTimeout(clearMutationTimer);
    clearMutationTimer = setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        scheduleEnhance();
        document.documentElement.classList.remove('vitalsop-cart-mutating');
      }));
    }, 80);
  }

  function isCartMutationUrl(value) {
    const url = typeof value === 'string' ? value : (value && value.url) || '';
    return /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/i.test(url);
  }

  function activeDrawerCandidates() {
    return [...document.querySelectorAll('.ocu-cart-drawer, #CartDrawer')].filter((el) => {
      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      return rect.left < window.innerWidth && rect.right > 0 && rect.top < window.innerHeight && rect.bottom > 0;
    });
  }

  function drawerShowsPaidLineNoEmptyState(variantId) {
    return activeDrawerCandidates().some((el) => {
      const text = el.innerText || '';
      if (/your cart is empty/i.test(text)) return false;
      return Boolean(el.querySelector(`[data-variant-id="${variantId}"]`)) || el.innerHTML.includes(String(variantId));
    });
  }

  function ensureSpinnerInDrawer(el) {
    if (!el || el.querySelector(':scope > .vitalsop-drawer-spinner')) return;
    const spinner = document.createElement('div');
    spinner.className = 'vitalsop-drawer-spinner';
    spinner.innerHTML = '<span class="vitalsop-drawer-spinner__dot"></span>';
    el.appendChild(spinner);
  }

  function beginDrawerRevealGate() {
    document.documentElement.classList.add('vitalsop-drawer-reveal-pending');
    document.querySelectorAll('.ocu-cart-drawer').forEach(ensureSpinnerInDrawer);
  }

  function endDrawerRevealGate() {
    document.documentElement.classList.remove('vitalsop-drawer-reveal-pending');
  }

  function buildCartRow(item, cart) {
    const row = document.createElement('div');
    row.className = 'vitalsop-cart-row';
    row.dataset.key = item.key;
    row.dataset.variantId = String(item.variant_id);
    if (item.properties) {
      try { row.dataset.properties = JSON.stringify(item.properties); } catch (_) {}
    }
    const offerText = item.selling_plan_allocation
      ? (item.selling_plan_allocation.selling_plan?.name || 'Subscription')
      : 'One-Time Purchase';
    row.innerHTML = `
      <div class="vitalsop-cart-row__media">
        ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="64" height="64">` : ''}
      </div>
      <div class="vitalsop-cart-row__details">
        <p class="vitalsop-cart-row__title"></p>
        <p class="vitalsop-cart-row__variant ocu-cart-line-item-product-variant"></p>
        <div class="vitalsop-cart-row__qty">
          <button type="button" class="vitalsop-qty-btn" data-action="decrease" aria-label="Decrease quantity">−</button>
          <span class="vitalsop-qty-value"></span>
          <button type="button" class="vitalsop-qty-btn" data-action="increase" aria-label="Increase quantity">+</button>
        </div>
      </div>
      <div class="vitalsop-cart-row__end">
        <button type="button" class="vitalsop-cart-row__remove" aria-label="Remove item">&times;</button>
        <span class="vitalsop-cart-row__price"></span>
      </div>
    `;
    row.querySelector('.vitalsop-cart-row__title').textContent = item.product_title || item.title;
    row.querySelector('.vitalsop-cart-row__variant').textContent = offerText;
    row.querySelector('.vitalsop-qty-value').textContent = item.quantity;
    row.querySelector('.vitalsop-cart-row__price').textContent = money(item.final_line_price, cart);
    return row;
  }

  function ensureCustomCartBody() {
    const cartDrawerRoot = document.getElementById('CartDrawer');
    if (!cartDrawerRoot) return null;
    let body = cartDrawerRoot.querySelector('.vitalsop-cart-body');
    if (body) return body;
    const innerDrawer = cartDrawerRoot.querySelector('.drawer__inner');
    if (!innerDrawer) return null;
    innerDrawer.querySelectorAll('.drawer__inner-empty, cart-drawer-items, .drawer__footer').forEach((el) => el.remove());
    body = document.createElement('div');
    body.className = 'vitalsop-cart-body';
    body.innerHTML = `
      <div class="vitalsop-cart-items"></div>
      <div class="vitalsop-cart-footer">
        <div class="vitalsop-cart-footer__row">
          <span>Subtotal</span>
          <span class="vitalsop-cart-footer__totals">
            <span class="vitalsop-cart-footer__badge"></span>
            <s class="vitalsop-cart-footer__compare"></s>
            <span class="vitalsop-cart-footer__total"></span>
          </span>
        </div>
        <button type="button" class="vitalsop-cart-footer__checkout">Checkout</button>
      </div>
    `;
    innerDrawer.appendChild(body);
    body.querySelector('.vitalsop-cart-footer__checkout').addEventListener('click', () => {
      continueToCheckout(null);
    });
    return body;
  }

  function renderDrawerItems(cart) {
    const body = ensureCustomCartBody();
    if (!body) return;
    const itemsContainer = body.querySelector('.vitalsop-cart-items');
    const signature = `${(cart.items || []).map((item) => `${item.key}:${item.quantity}`).join('|')}|${cart.total_price}`;
    if (itemsContainer.dataset.signature === signature) return;
    itemsContainer.dataset.signature = signature;
    itemsContainer.innerHTML = '';
    const isEmpty = !(cart.items || []).length;
    if (isEmpty) {
      const empty = document.createElement('div');
      empty.className = 'vitalsop-cart-empty';
      empty.innerHTML = `
        <p class="vitalsop-cart-empty__text">Your cart is empty</p>
        <a href="${window.Shopify?.routes?.root || '/'}" class="vitalsop-cart-empty__link">Continue Shopping</a>
      `;
      itemsContainer.appendChild(empty);
    } else {
      (cart.items || []).forEach((item) => {
        itemsContainer.appendChild(buildCartRow(item, cart));
      });
    }
    body.classList.toggle('vitalsop-cart-body--empty', isEmpty);
    updateFooterSubtotal(body, cart);
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) cartDrawerEl.classList.toggle('is-empty', isEmpty);
  }

  function computeSubtotalBreakdown(cart) {
    let compareCents = 0;
    let finalCents = 0;
    (cart.items || []).forEach(item => {
      if (GIFT_CART_VARIANTS.has(String(item.variant_id))) return;
      const variantId = String(item.variant_id);
      const hasPlan = Boolean(item.selling_plan_allocation);
      // VitalSop's subscription discount isn't a native Shopify "compare at
      // price" on the variant - it's just a lower price on the selling plan
      // - so those two lines need an explicit reference price. Every other
      // product (including future ones) falls back to Shopify's own
      // original_line_price, which already reflects any real compare-at
      // price set on that product's variant with zero extra config here.
      let compareLine = item.original_line_price ?? item.final_line_price;
      if (hasPlan && variantId === CART_UPGRADES.bestValue.variantId) {
        compareLine = CART_UPGRADES.bestValue.comparePriceCents * item.quantity;
      } else if (hasPlan && variantId === CART_UPGRADES.subscribe.variantId) {
        compareLine = CART_UPGRADES.subscribe.comparePriceCents * item.quantity;
      }
      compareCents += compareLine;
      finalCents += item.final_line_price;
    });
    return { compareCents, finalCents };
  }

  function updateFooterSubtotal(body, cart) {
    const totalEl = body.querySelector('.vitalsop-cart-footer__total');
    if (!totalEl) return;
    const compareEl = body.querySelector('.vitalsop-cart-footer__compare');
    const badgeEl = body.querySelector('.vitalsop-cart-footer__badge');
    const { compareCents, finalCents } = computeSubtotalBreakdown(cart);
    totalEl.textContent = money(finalCents, cart);
    const hasDiscount = compareCents > finalCents;
    if (compareEl) {
      compareEl.textContent = money(compareCents, cart);
      compareEl.style.display = hasDiscount ? 'inline' : 'none';
    }
    if (badgeEl) {
      const offDollars = Math.round((compareCents - finalCents) / 100);
      badgeEl.textContent = `$${offDollars} Off`;
      badgeEl.style.display = hasDiscount ? 'inline-block' : 'none';
    }
  }

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('.vitalsop-qty-btn, .vitalsop-cart-row__remove');
    if (!btn) return;
    const row = btn.closest('.vitalsop-cart-row');
    if (!row) return;
    event.preventDefault();
    const key = row.dataset.key;
    const currentQty = Number(row.querySelector('.vitalsop-qty-value')?.textContent || 1);
    const newQty = btn.classList.contains('vitalsop-cart-row__remove')
      ? 0
      : Math.max(0, currentQty + (btn.dataset.action === 'increase' ? 1 : -1));
    try {
      const cart = await fetchCartJson('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      if (cart) enhanceCart(cart);
    } catch (changeError) {
      console.warn('[VitalSop cart] Quantity change failed', changeError);
    }
  }, true);

  async function refreshNativeCartDrawer(cart) {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return false;
    renderDrawerItems(cart);
    cartDrawerEl.classList.remove('is-empty');
    cartDrawerEl.open();
    syncAliaWidgetVisibility();
    return true;
  }

  async function revealDrawer(radio, cart) {
    const ocu = window.Zipify?.Cart?.instance;
    if (ocu?.updateCart || ocu?.openCart || ocu?.openUpdatedCart) {
      beginDrawerRevealGate();
      try {
        if (ocu.updateCart) await Promise.resolve(ocu.updateCart());
        if (ocu.openCart) ocu.openCart({ preventUpdate: true });
        else if (ocu.openUpdatedCart) ocu.openUpdatedCart();
        syncAliaWidgetVisibility();
        await waitForStableCondition(() => drawerShowsPaidLineNoEmptyState(radio.dataset.variantId), 4000);
      } finally {
        endDrawerRevealGate();
      }
    } else {
      await refreshNativeCartDrawer(cart);
    }
    if (cart) enhanceCart(cart);
  }

  function waitForStableCondition(check, timeoutMs = 1500) {
    return new Promise((resolve) => {
      const start = performance.now();
      let stableFrames = 0;
      function frame() {
        if (check()) {
          stableFrames += 1;
          if (stableFrames >= 2) {
            resolve(true);
            return;
          }
        } else {
          stableFrames = 0;
        }
        if (performance.now() - start > timeoutMs) {
          resolve(false);
          return;
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    });
  }

  async function reconcileGiftLines(options = {}) {
    const { notify = true } = options;
    if (giftCleanupRunning) {
      while (giftCleanupRunning) await new Promise(resolve => setTimeout(resolve, 25));
      return fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
    }

    giftCleanupRunning = true;
    try {
      let cart = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      // Welcome-gift auto-adds are retired - this only cleans up any
      // legacy gift line items still sitting in a returning customer's cart.
      const updates = {};
      (cart.items || []).forEach(item => {
        const variantId = String(item.variant_id);
        if (GIFT_CART_VARIANTS.has(variantId) && Number(item.quantity) !== 0) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return cart;
      markCartMutating();

      cart = await fetchCartJson('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!cart?.token || !Array.isArray(cart.items)) {
        cart = await fetchCartJson('/cart.js', { headers: { Accept: 'application/json' } });
      }

      if (notify) {
        const ocu = window.Zipify?.Cart?.instance;
        if (ocu?.updateCart) await Promise.resolve(ocu.updateCart()).catch(() => {});
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart }
        }));
      }
      scheduleEnhance();
      return cart;
    } catch (error) {
      console.warn('[VitalSop gifts] Unable to reconcile welcome gifts', error);
      return null;
    } finally {
      giftCleanupRunning = false;
      clearCartMutatingSoon();
    }
  }

  function scheduleGiftCleanup() {
    clearTimeout(giftCleanupTimer);
    giftCleanupTimer = setTimeout(async () => {
      if (bundleSubmitting) {
        scheduleGiftCleanup();
        return;
      }
      await reconcileGiftLines();
    }, 450);
  }

  async function continueToCheckout(trigger) {
    while (bundleSubmitting) await new Promise(resolve => setTimeout(resolve, 25));
    await reconcileGiftLines();
    checkoutResuming = true;
    try {
      if (trigger?.click) trigger.click();
      else window.location.assign('/checkout');
    } finally {
      setTimeout(() => { checkoutResuming = false; }, 0);
    }
  }

  document.addEventListener('click', event => {
    if (checkoutResuming) return;
    const trigger = event.target.closest('.ocu-checkout-button, [name="checkout"], a[href*="/checkout"]');
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueToCheckout(trigger);
  }, true);

  document.addEventListener('submit', event => {
    if (checkoutResuming) return;
    const form = event.target;
    if (!(form?.matches?.('#ocu-cart-form, form[action*="/checkout"]'))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueToCheckout(event.submitter);
  }, true);

  function cartMutationSettled() {
    clearCartMutatingSoon();
    scheduleGiftCleanup();
  }

  const nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (...args) {
      if (!isCartMutationUrl(args[0])) return nativeFetch.apply(this, args);
      markCartMutating();
      return nativeFetch.apply(this, args).finally(cartMutationSettled);
    };
  }

  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__vitalsopCartMutation = isCartMutationUrl(url);
    return nativeXhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__vitalsopCartMutation) {
      markCartMutating();
      this.addEventListener('loadend', cartMutationSettled, { once: true });
    }
    return nativeXhrSend.apply(this, args);
  };

  function selectedManagedOffer() {
    const radio = document.querySelector('.offer-card__radio-input:checked');
    if (!radio || !OFFER_VARIANTS.has(String(radio.dataset.variantId))) return null;
    if (radio.dataset.onetime !== 'true' && !radio.dataset.planId) return null;
    return radio;
  }

  function giftItemsForOffer(radio) {
    // Welcome-gift auto-adds are retired - offers no longer bundle a
    // physical gift line item into the cart.
    return [];
  }

  async function fetchCartJson(url, options) {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const response = await nativeFetch(url, options);
      if (response.ok) return response.json();
      const detail = await response.text().catch(() => '');
      if (response.status !== 429 || attempt === 2) {
        throw new Error(`Cart request failed (${response.status}) ${detail}`.trim());
      }
      await new Promise(resolve => setTimeout(resolve, 600 * (2 ** attempt)));
    }
    return null;
  }

  async function replaceOfferBundle(radio, button) {
    if (bundleSubmitting) return;
    bundleSubmitting = true;
    markCartMutating();
    if (button) {
      button.dataset.originalText = button.textContent;
      button.textContent = radio.dataset.cartUpgrade === 'true' ? 'Upgrading…' : 'Adding…';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('vitalsop-btn-loading');
    }

    let tracking = null;
    try {
      const current = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const oldMain = (current.items || []).find(item => (
        Number(item.product_id) === VITALSOP_PRODUCT_ID
        && !GIFT_CART_VARIANTS.has(String(item.variant_id))
      ));
      if (radio.dataset.cartUpgrade === 'true') {
        tracking = {
          upgrade_id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          clicked_at: new Date().toISOString(),
          from_variant_id: String(oldMain?.variant_id || ''),
          from_selling_plan: oldMain?.selling_plan_allocation?.selling_plan?.name || 'one-time',
          from_line_revenue_cents: Number(oldMain?.final_line_price || 0),
          target_variant_id: String(radio.dataset.variantId),
          target_selling_plan_id: String(radio.dataset.planId || ''),
          target_offer: radio.dataset.supplyDays === '90' ? '90-day subscription' : '30-day subscription'
        };
        trackUpgradeEvent('VitalSop Cart Upgrade Clicked', tracking, 'clicked');
      }
      const stableLineIdentity = item => JSON.stringify({
        variantId: String(item.variant_id),
        sellingPlanId: String(item.selling_plan_allocation?.selling_plan?.id || ''),
        properties: Object.entries(item.properties || {})
          .filter(([, value]) => value != null && value !== '')
          .sort(([left], [right]) => left.localeCompare(right))
      });
      const oldManagedLines = [];
      (current.items || []).forEach(item => {
        if (!MANAGED_CART_VARIANTS.has(String(item.variant_id))) return;
        oldManagedLines.push({
          key: item.key,
          identity: stableLineIdentity(item),
          remaining: Math.max(0, Number(item.quantity) || 0)
        });
      });

      const parentItem = {
        id: Number(radio.dataset.variantId),
        quantity: 1
      };
      if (radio.dataset.planId) parentItem.selling_plan = Number(radio.dataset.planId);
      if (tracking) {
        parentItem.properties = {
          _vitalsop_cart_upgrade: 'true',
          _vitalsop_upgrade_id: tracking.upgrade_id,
          _vitalsop_upgrade_from_variant: tracking.from_variant_id,
          _vitalsop_upgrade_from_plan: tracking.from_selling_plan,
          _vitalsop_upgrade_from_revenue_cents: String(tracking.from_line_revenue_cents),
          _vitalsop_upgrade_to: tracking.target_offer,
          _vitalsop_upgrade_at: tracking.clicked_at,
          _vitalsop_tracking_started_at: UPGRADE_TRACKING_STARTED_AT
        };
      }
      const items = [parentItem, ...giftItemsForOffer(radio)];

      await fetchCartJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      let cart = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const oldLineUpdates = {};
      (cart.items || []).forEach(item => {
        if (!MANAGED_CART_VARIANTS.has(String(item.variant_id))) return;
        const identity = stableLineIdentity(item);
        const oldLine = oldManagedLines.find(line => line.remaining > 0 && line.key === item.key)
          || oldManagedLines.find(line => line.remaining > 0 && line.identity === identity);
        if (!oldLine) return;
        const currentQuantity = Math.max(0, Number(item.quantity) || 0);
        const quantityToRemove = Math.min(currentQuantity, oldLine.remaining);
        oldLineUpdates[item.key] = currentQuantity - quantityToRemove;
        oldLine.remaining -= quantityToRemove;
      });
      if (Object.keys(oldLineUpdates).length) {
        cart = await fetchCartJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: oldLineUpdates })
        });
      }

      if (!cart?.token || !Array.isArray(cart.items)) {
        cart = await fetchCartJson('/cart.js', { headers: { Accept: 'application/json' } });
      }
      cart = await reconcileGiftLines({ notify: false }) || cart;
      const upgradedLine = (cart.items || []).find(item => (
        String(item.variant_id) === String(radio.dataset.variantId)
        && (
          !radio.dataset.planId
          || String(item.selling_plan_allocation?.selling_plan?.id || '') === String(radio.dataset.planId)
        )
      ));
      if (!upgradedLine) throw new Error('The upgraded offer was not confirmed in the Shopify cart.');

      if (tracking) {
        const upgradedRevenueCents = Number(upgradedLine.final_line_price || 0);
        const incrementalRevenueCents = Math.max(
          0,
          upgradedRevenueCents - tracking.from_line_revenue_cents
        );
        trackUpgradeEvent('VitalSop Cart Upgrade Completed', {
          ...tracking,
          completed_at: new Date().toISOString(),
          upgraded_line_revenue_cents: upgradedRevenueCents,
          incremental_revenue_cents: incrementalRevenueCents,
          $value: incrementalRevenueCents / 100,
          value: incrementalRevenueCents / 100,
          currency: cart.currency || 'USD'
        }, 'completed');
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        await revealDrawer(radio, cart);
      } catch (refreshError) {
        console.warn('[VitalSop bundle] Bundle added, but the cart drawer refresh failed', refreshError);
      }
    } catch (error) {
      console.error('[VitalSop bundle] Unable to add offer bundle', error);
      if (tracking) {
        trackUpgradeEvent('VitalSop Cart Upgrade Failed', {
          ...tracking,
          failed_at: new Date().toISOString(),
          error: String(error?.message || error).slice(0, 250)
        });
      }
      window.alert('We could not add your VitalSop bundle. Please try again.');
    } finally {
      bundleSubmitting = false;
      if (button) {
        button.textContent = button.dataset.originalText || 'Upgrade & Save';
        delete button.dataset.originalText;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('vitalsop-btn-loading');
      }
      clearCartMutatingSoon();
    }
  }

  function interceptManagedAdd(event, button, form) {
    const radio = selectedManagedOffer();
    if (!radio) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    replaceOfferBundle(radio, button || form?.querySelector('[type="submit"]'));
    return true;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('.product-form__submit, [name="add"], #sticky-atc-btn');
    if (!button) return;
    const form = button.closest('form') || document.querySelector('form[action*="/cart/add"]');
    interceptManagedAdd(event, button, form);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    interceptManagedAdd(event, form.querySelector('[type="submit"]'), form);
  }, true);

  function money(cents, cartOverride) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (cartOverride || currentCart())?.currency || 'USD'
    }).format(cents / 100);
  }

  function cartItemRows() {
    return [...document.querySelectorAll('.ocu-cart-line-item-wrapper, #CartDrawer .vitalsop-cart-row')];
  }

  function rowVariantId(row) {
    if (row.dataset.variantId) return String(row.dataset.variantId);
    if (row.dataset.key) return String(row.dataset.key).split(':')[0];
    const el = row.querySelector('[data-variant-id]');
    return el ? String(el.dataset.variantId) : '';
  }

  function selectedOfferText(item) {
    if (!item.selling_plan_allocation) return 'One-Time Purchase';
    if (String(item.variant_id) === CART_UPGRADES.bestValue.variantId) {
      return 'Buy 2 Get 1 Free';
    }
    return '30-Day Subscription';
  }

  function updateCadence(cart) {
    cartItemRows().forEach((row) => {
      const variantId = rowVariantId(row);
      const item = cart.items?.find(entry => String(entry.variant_id) === variantId);
      if (!item || Number(item.product_id) !== VITALSOP_PRODUCT_ID) return;

      const variantLabel = row.querySelector('.ocu-cart-line-item-product-variant');
      if (variantLabel) {
        const offerText = selectedOfferText(item);
        if (variantLabel.textContent.trim() !== offerText) variantLabel.textContent = offerText;
      }

      const isBestValue = String(item.variant_id) === CART_UPGRADES.bestValue.variantId
        && Boolean(item.selling_plan_allocation);
      let savings = row.querySelector('.vitalsop-cart-savings');
      if (!isBestValue) {
        if (savings) savings.remove();
        return;
      }
      const savingsCents = Math.max(
        0,
        CART_UPGRADES.bestValue.comparePriceCents * item.quantity - item.final_line_price
      );
      const savingsText = `You save $${Math.round(savingsCents / 100)}`;
      if (!savings) {
        const anchor = variantLabel || row.querySelector('.cart-item__details');
        if (!anchor) return;
        savings = document.createElement('div');
        savings.className = 'vitalsop-cart-savings';
        savings.innerHTML = `
          <strong>${savingsText}</strong>
          <span>Your FREE 3rd bottle is already included</span>
        `;
        if (variantLabel) variantLabel.insertAdjacentElement('afterend', savings);
        else anchor.appendChild(savings);
      } else {
        const strong = savings.querySelector('strong');
        if (strong && strong.textContent !== savingsText) strong.textContent = savingsText;
      }
    });
  }

  function ensureShippingBanner() {
    let banner = document.querySelector('.ocu-cart-banner, .vitalsop-shipping-banner');
    if (banner) return banner;
    const header = document.querySelector('#CartDrawer .drawer__header');
    if (!header) return null;
    banner = document.createElement('div');
    banner.className = 'vitalsop-shipping-banner';
    banner.innerHTML = '<p class="vitalsop-shipping-banner__message"></p><div class="vitalsop-shipping-progress"><span></span></div>';
    header.insertAdjacentElement('afterend', banner);
    return banner;
  }

  function fitBannerMessageOneLine(message) {
    const maxFontPx = 16;
    const minFontPx = 10;
    message.style.fontSize = `${maxFontPx}px`;
    let fontSize = maxFontPx;
    while (message.scrollWidth > message.clientWidth && fontSize > minFontPx) {
      fontSize -= 0.5;
      message.style.fontSize = `${fontSize}px`;
    }
  }

  function updateShipping(cart) {
    if (!cart.items || !cart.items.length) {
      const existing = document.querySelector('.vitalsop-shipping-banner');
      if (existing) existing.remove();
      return;
    }
    const banner = ensureShippingBanner();
    if (!banner) return;
    const message = banner.querySelector('.ocu-cart-banner--message-text, .vitalsop-shipping-banner__message');
    if (!message) return;

    const subtotal = Number(cart.total_price ?? cart.items_subtotal_price ?? 0);
    const subscriptionQualifies = Boolean(
      cart.items?.some(item => item.selling_plan_allocation)
    );
    const remaining = subscriptionQualifies
      ? 0
      : Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
    const progress = subscriptionQualifies
      ? 100
      : Math.min(100, Math.max(0, subtotal / FREE_SHIPPING_THRESHOLD * 100));
    const text = remaining > 0
      ? `You're ${money(remaining, cart)} away from FREE SHIPPING`
      : "🎉 Congrats! You've unlocked FREE SHIPPING!";

    if (message.textContent !== text) {
      message.textContent = text;
      fitBannerMessageOneLine(message);
    }
    let bar = banner.querySelector('.vitalsop-shipping-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'vitalsop-shipping-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span></span>';
      banner.appendChild(bar);
    }
    const fill = bar.firstElementChild;
    const width = `${progress.toFixed(2)}%`;
    if (fill.style.width !== width) fill.style.width = width;
  }

  function updateTotalsPresentation() {
    const label = document.querySelector(
      '.ocu-estimated-info > .ocu-editable-cart-text:first-child span'
    );
    if (label && label.textContent.trim() !== 'Subtotal') {
      label.textContent = 'Subtotal';
    }
  }

  function updateGiftPresentation(cart) {
    cartItemRows().forEach((row) => {
      const variantId = rowVariantId(row);
      let properties = {};
      try {
        properties = JSON.parse(row.dataset.properties || '{}');
      } catch (_) {}
      const rowText = row.textContent || '';
      const isWelcomeGift = properties._vitalsop_welcome_gift === 'true'
        || Boolean(properties._vitalsop_welcome_gift_for)
        || GIFT_CART_VARIANTS.has(variantId)
        || /Counter\s*top reminder|Ritual tray|Travel Pouch/i.test(rowText);
      row.classList.toggle('vitalsop-ocu-hidden-gift', isWelcomeGift);
      row.classList.toggle('vitalsop-hidden-gift-row', isWelcomeGift);
      if (isWelcomeGift) row.setAttribute('aria-hidden', 'true');
      else row.removeAttribute('aria-hidden');
    });

    const visibleQuantity = (cart.items || []).reduce((total, item) => {
      return GIFT_CART_VARIANTS.has(String(item.variant_id))
        ? total
        : total + Math.max(1, Number(item.quantity) || 1);
    }, 0);
    const visibleText = String(visibleQuantity);
    const count = document.querySelector('.ocu-cart-header--items-count span');
    if (count && count.textContent !== visibleText) count.textContent = visibleText;
    const accessibleCount = document.querySelector('[data-testid="cart-count"]');
    const accessibleText = `${visibleQuantity} ${visibleQuantity === 1 ? 'item' : 'items'}`;
    if (accessibleCount && accessibleCount.textContent !== accessibleText) {
      accessibleCount.textContent = accessibleText;
    }
    const themeCount = document.querySelector('.cart-count-bubble');
    if (themeCount) {
      const visibleCount = themeCount.querySelector('span[aria-hidden="true"]');
      const hiddenCount = themeCount.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = visibleText;
      if (hiddenCount) hiddenCount.textContent = accessibleText;
      themeCount.style.display = visibleQuantity > 0 ? '' : 'none';
    }
  }

  function updateUpgradePrompt(cart) {
    const ocuContent = document.querySelector('.ocu-cart-content--container');
    const lineItemsContainer = ocuContent?.querySelector('.ocu-cart-line-items')
      || document.querySelector('#CartDrawer .vitalsop-cart-items');
    let prompt = document.querySelector('.vitalsop-cart-upgrade');
    const item = (cart.items || []).find(entry => (
      Number(entry.product_id) === VITALSOP_PRODUCT_ID
      && !GIFT_CART_VARIANTS.has(String(entry.variant_id))
    ));

    let upgrade = null;
    if (item) {
      const isBestValue = String(item.variant_id) === CART_UPGRADES.bestValue.variantId
        && Boolean(item.selling_plan_allocation);
      if (!isBestValue) upgrade = CART_UPGRADES.bestValue;
    }

    if (!lineItemsContainer || !upgrade) {
      if (prompt) prompt.remove();
      return;
    }

    if (!prompt) {
      prompt = document.createElement('section');
      prompt.className = 'vitalsop-cart-upgrade';
      prompt.setAttribute('aria-label', 'Upgrade your VitalSop order');
      lineItemsContainer.insertAdjacentElement('afterend', prompt);
    }
    if (prompt.dataset.targetVariant === upgrade.variantId) return;

    prompt.dataset.targetVariant = upgrade.variantId;
    prompt.innerHTML = `
      <p class="vitalsop-cart-upgrade__badge"></p>
      <h3 class="vitalsop-cart-upgrade__title"></h3>
      <ul class="vitalsop-cart-upgrade__benefits"></ul>
      <button type="button" class="vitalsop-cart-upgrade__button"></button>
    `;
    prompt.querySelector('.vitalsop-cart-upgrade__badge').textContent = upgrade.badge;
    prompt.querySelector('.vitalsop-cart-upgrade__title').textContent = upgrade.title;
    const benefits = prompt.querySelector('.vitalsop-cart-upgrade__benefits');
    upgrade.benefits.forEach(text => {
      const benefit = document.createElement('li');
      benefit.textContent = text;
      benefits.appendChild(benefit);
    });
    const button = prompt.querySelector('.vitalsop-cart-upgrade__button');
    button.textContent = upgrade.button;
    trackUpgradeEvent('VitalSop Cart Upgrade Viewed', {
      target_variant_id: upgrade.variantId,
      target_selling_plan_id: upgrade.planId,
      target_offer: upgrade.supplyDays === '90' ? '90-day subscription' : '30-day subscription',
      target_line_revenue_cents: upgrade.supplyDays === '90' ? 7995 : 3995
    }, 'viewed');
    button.addEventListener('click', () => replaceOfferBundle({
      dataset: { ...upgrade, cartUpgrade: 'true' }
    }, button));
  }

  function enhanceCart(cart) {
    if (!cart) return;
    if (!document.querySelector('.ocu-cart-container') && !document.querySelector('#CartDrawer')) return;
    lastKnownCart = cart;
    if (!document.querySelector('.ocu-cart-container')) renderDrawerItems(cart);
    updateShipping(cart);
    updateCadence(cart);
    updateTotalsPresentation();
    updateGiftPresentation(cart);
    updateUpgradePrompt(cart);
  }

  let aliaDrawerObserverAttached = false;
  let aliaEnforceInterval = null;
  let lastFullSweepAt = 0;

  let scrollLockY = null;

  function syncAliaWidgetVisibility() {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && !aliaDrawerObserverAttached) {
      aliaDrawerObserverAttached = true;
      new MutationObserver(syncAliaWidgetVisibility).observe(cartDrawerEl, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    const isOpen = Boolean(cartDrawerEl?.classList.contains('active'));

    if (isOpen && scrollLockY === null) {
      scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollLockY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else if (!isOpen && scrollLockY !== null) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollLockY);
      scrollLockY = null;
    }

    document.querySelectorAll('[id^="alia-root"]').forEach((el) => {
      if (isOpen) el.style.setProperty('display', 'none', 'important');
      else if (el.style.getPropertyValue('display') === 'none') el.style.removeProperty('display');
    });

    const now = Date.now();
    if (isOpen && now - lastFullSweepAt >= 280) {
      lastFullSweepAt = now;
      document.querySelectorAll('*').forEach((el) => {
        if (el === cartDrawerEl || el.id === 'CartDrawer') return;
        if (cartDrawerEl && (el.contains(cartDrawerEl) || cartDrawerEl.contains(el))) return;
        if (el.dataset.vitalsopHiddenDisplay !== undefined) return;
        const style = getComputedStyle(el);
        if (style.position !== 'fixed') return;
        const zIndex = parseInt(style.zIndex, 10) || 0;
        if (zIndex < 999999) return;
        el.dataset.vitalsopHiddenDisplay = el.style.getPropertyValue('display') || '';
        el.style.setProperty('display', 'none', 'important');
      });
    } else if (!isOpen) {
      document.querySelectorAll('[data-vitalsop-hidden-display]').forEach((el) => {
        const previous = el.dataset.vitalsopHiddenDisplay;
        if (previous) el.style.setProperty('display', previous);
        else el.style.removeProperty('display');
        delete el.dataset.vitalsopHiddenDisplay;
      });
    }

    if (isOpen && !aliaEnforceInterval) {
      aliaEnforceInterval = setInterval(syncAliaWidgetVisibility, 250);
    } else if (!isOpen && aliaEnforceInterval) {
      clearInterval(aliaEnforceInterval);
      aliaEnforceInterval = null;
    }
  }

  function scheduleEnhance() {
    syncAliaWidgetVisibility();
    if (bundleSubmitting) return;
    cancelAnimationFrame(enhanceFrame);
    enhanceFrame = requestAnimationFrame(async () => {
      const cart = await fetchCartJson('/cart.js', { headers: { Accept: 'application/json' } }).catch(() => null);
      if (cart) enhanceCart(cart);
    });
  }

  new MutationObserver(scheduleEnhance).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  document.addEventListener('DOMContentLoaded', () => {
    scheduleEnhance();
    scheduleGiftCleanup();
  });
  window.addEventListener('pageshow', () => {
    scheduleEnhance();
    scheduleGiftCleanup();
  });
})();
